import { useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../components/HeaderBar";
import DashboardTour from "../components/DashboardTour";
import themeColors from "../theme";
import {
    CMEFundsIcon,
    CMECreditsIcon,
    VaultIcon,
    MarketplaceIcon,
    ClinicalUpdatesIcon
} from "../components/icons/DashboardIcons";

const ONBOARDED_KEY = "dashboard.onboarded.v1";

export default function Dashboard() {
    const navigate = useNavigate();
    const { teal, lightGray } = themeColors;

    const tiles = [
      {
        title: "CME Funds",
        description: "Track spending and remaining CME funds",
        route: "/purchases",
        icon: <CMEFundsIcon size={80} />,
        linkText: "View funds",
        tourText: "CME Fund Tracker lets you enter your annual CME allowance and track all CME expenses in one place.",
      },
      {
        title: "CME Credits",
        description: "View requirements and stay compliant",
        route: "/creditTracker",
        icon: <CMECreditsIcon size={80} />,
        linkText: "View credits",
        tourText: "CME Credit Tracker lets you select your state, view CME requirements, and track your completed credits with uploaded certificates.",
      },
      {
        title: "Marketplace",
        description: "Browse high-quality CME resources",
        route: "/marketplace",
        icon: <MarketplaceIcon size={80} />,
        linkText: "Browse CME",
        tourText: "Browse the Marketplace to find CME-eligible purchases including educational resources, conferences, and essential clinical tools.",
      },
      {
        title: "Vault",
        description: "Store license and important documents",
        route: "/vault",
        icon: <VaultIcon size={80} />,
        linkText: "Access Vault",
        tourText: "Upload and manage your credentials in the Vault, a secure encrypted hub that lets you store, organize, and send documents like certificates and diplomas.",
      },
      {
        title: "Clinical Updates",
        description: "Stay up to date on practice-changing guidelines in Emergency Medicine",
        route: "/clinical-updates",
        icon: <ClinicalUpdatesIcon size={80} />,
        linkText: "View Updates",
        tourText: "Click here to view the latest evidence, guidelines, and clinical updates relevant to emergency medicine.",
      },
    ];

    // First-time orientation tour ---------------------------------------------
    const tileRefs = useRef([]);
    const [tourStep, setTourStep] = useState(() =>
        localStorage.getItem(ONBOARDED_KEY) === "true" ? -1 : 0
    );
    const [targetRect, setTargetRect] = useState(null);
    const tourActive = tourStep >= 0 && tourStep < tiles.length;

    const finishTour = useCallback(() => {
        localStorage.setItem(ONBOARDED_KEY, "true");
        setTourStep(-1);
    }, []);

    const handleTourNext = useCallback(() => {
        if (tourStep >= tiles.length - 1) {
            finishTour();
        } else {
            setTourStep((prev) => prev + 1);
        }
    }, [tourStep, tiles.length, finishTour]);

    useEffect(() => {
        if (!tourActive) return;
        const el = tileRefs.current[tourStep];
        if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });

        const measure = () => {
            const node = tileRefs.current[tourStep];
            if (node) setTargetRect(node.getBoundingClientRect());
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [tourStep, tourActive]);

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: lightGray }}>
            <HeaderBar />

            <Box sx={{ px: { xs: 2, md: 8 }, py: 6, maxWidth: "900px", mx: "auto" }}>
                <Typography variant="h3" fontWeight="bold" color="#2C3E50" textAlign="center" mb={1}>
                    Dashboard
                </Typography>
                <Typography variant="body1" color="#6C757D" textAlign="center" mb={6}>
                    Everything related to your CME, in one place.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {tiles.map((tile, index) => (
                        <Box
                            key={tile.title}
                            ref={(el) => (tileRefs.current[index] = el)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                py: 3,
                                px: 2,
                                borderBottom: index < tiles.length - 1 ? "1px solid #E0E0E0" : "none",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.02)"
                                }
                            }}
                            onClick={() => {
                                if (tourActive) return;
                                navigate(tile.route);
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                                <Box sx={{
                                    width: 80,
                                    height: 80,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0
                                }}>
                                    {tile.icon}
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" color="#2C3E50">
                                        {tile.title}
                                    </Typography>
                                    <Typography variant="body2" color="#6C757D">
                                        {tile.description}
                                    </Typography>
                                </Box>
                            </Box>
                            <Link
                                component="span"
                                sx={{
                                    color: teal,
                                    textDecoration: "none",
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                        textDecoration: "underline"
                                    }
                                }}
                            >
                                → {tile.linkText}
                            </Link>
                        </Box>
                    ))}
                </Box>
            </Box>

            {tourActive && (
                <DashboardTour
                    steps={tiles.map((t) => ({ title: t.title, text: t.tourText }))}
                    stepIndex={tourStep}
                    targetRect={targetRect}
                    onNext={handleTourNext}
                    onClose={finishTour}
                />
            )}
        </Box>
    );
}
